import { plural } from "@/components/common/setUtils";
import type {
  DraftTable,
  RoundCost,
  UsageSplit,
  DraftVersion,
  TableFeedback,
  Verdict,
} from "@/lib/api/apps/types";

/** The column a link to `table` lands on: its key, else its first column. */
export const linkTarget = (table: DraftTable): string | undefined =>
  (table.columns.find((c) => c.key) ?? table.columns[0])?.name;

/** "Table.column" for where a link to `table` lands. */
export const linkEndLabel = (table: DraftTable): string => {
  const column = linkTarget(table);
  return column ? `${table.name}.${column}` : table.name;
};

export interface LinkEnd {
  table: DraftTable;
  column: string;
}

/** The other ends of a column's links, both ways: the key it points at, and
 *  every column that points at it when it is where its table's links land. */
export function linkEnds(
  tables: DraftTable[],
  tableId: string,
  column: string,
): LinkEnd[] {
  const table = tables.find((t) => t.id === tableId);
  const col = table?.columns.find((c) => c.name === column);
  if (!table || !col) return [];
  const to = tables
    .filter((t) => t.id === col.link_to_table)
    .map((t) => ({ table: t, column: linkTarget(t) ?? "" }));
  const from =
    column === linkTarget(table)
      ? tables.flatMap((t) =>
          t.columns
            .filter((c) => c.link_to_table === tableId)
            .map((c) => ({ table: t, column: c.name })),
        )
      : [];
  return [...to, ...from];
}

/** A table's verdict as shown: the one picked, else Change once anything was written on it. */
export const shownVerdict = (f: TableFeedback): Verdict | null =>
  f.verdict ??
  (f.note.trim() ||
  f.drop_columns.length > 0 ||
  Object.values(f.column_notes ?? {}).some((n) => n.trim())
    ? "change"
    : null);

/** Whether a table carries a mark: a verdict, or anything written on it. */
export const isMarked = (f: TableFeedback | undefined): f is TableFeedback =>
  !!f && shownVerdict(f) !== null;

/** The remarks a table carries beyond its verdict: its note, the columns it drops, its column comments. */
export const remarkCount = (f: TableFeedback) =>
  (f.note.trim() ? 1 : 0) +
  f.drop_columns.length +
  Object.values(f.column_notes ?? {}).filter((n) => n.trim()).length;

export function saysAnything(
  fb: Record<string, TableFeedback>,
  note: string,
): boolean {
  return (
    note.trim().length > 0 ||
    Object.values(fb).some(
      (f) =>
        // A locked table rides along, but asks the agent for nothing.
        (f.verdict && f.verdict !== "keep") ||
        f.drop_columns.length > 0 ||
        f.note.trim().length > 0 ||
        Object.values(f.column_notes ?? {}).some((n) => n.trim().length > 0),
    )
  );
}

/** Tables the previous version had and this one does not. */
export function removedIn(
  version: DraftVersion,
  previous: DraftVersion | undefined,
): DraftTable[] {
  if (!previous) return [];
  return previous.tables.filter(
    (t) => !version.tables.some((x) => x.id === t.id),
  );
}

/** What an Apply left undone and what it found in the rows, as sentences. */
export function applyOutcome(version: DraftVersion | undefined): string {
  const missed = version?.apply?.not_applied ?? [];
  return [
    missed.length &&
      `${plural(missed.length, "change")} not applied: ${missed[0].why}.`,
    ...(version?.apply?.reports ?? []).map((r) => `${r}.`),
  ]
    .filter(Boolean)
    .join(" ");
}

const KINDS: { key: keyof Omit<UsageSplit, "price">; label: string }[] = [
  { key: "input", label: "Input" },
  { key: "cache_write", label: "Cache write" },
  { key: "cache_read", label: "Cache read" },
  { key: "output", label: "Output" },
];

/** Tokens by kind, each at the rate its round was charged. */
export function splitUsage(costs: RoundCost[]) {
  const lines = KINDS.map(({ label }) => ({ label, tokens: 0, micros: 0 }));
  let micros = 0;
  for (const cost of costs) {
    micros += cost.micros;
    KINDS.forEach(({ key }, i) => {
      const n = cost.usage?.[key] ?? 0;
      lines[i].tokens += n;
      lines[i].micros += n * (cost.usage?.price[key] ?? 0);
    });
  }
  return { lines: lines.filter((l) => l.tokens > 0), micros };
}
